import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { PlatformEnum } from '@panamax/app-state';
import {
  PaymentFilter,
  PaymentFilterIds,
} from '@payments/pages/payment-activity/services/payment-activity.service';
import {
  AllFilterIds,
  FilterBaseVM,
  ModalFilterService,
  ModalType,
  PaymentsModalService,
} from '@payments/services/payments-modal.service';
import { QuickFilter } from '@shared/components/usf-filtering-banner/model/quick-filter.model';
import { Filter, InvoiceFilterIds } from '@usf/ngrx-payment';
import { Status } from '@usf/payment-types';
import { Observable, Subscription } from 'rxjs';

const FiltersIds = { ...InvoiceFilterIds, ...PaymentFilterIds };

export enum HeaderFilterButtonStatus {
  enabled,
  disabled,
  hidden,
}

@Component({
  selector: 'app-header-filters',
  templateUrl: './header-filters.component.html',
  styleUrls: ['./header-filters.component.scss'],
})
export class HeaderFiltersComponent implements OnInit, OnDestroy {
  @Input() isTouch: boolean;
  @Input() breadcrumbs = [];
  @Input() service: ModalFilterService;
  @Input() filters: Filter[] | PaymentFilter[];
  @Input() showQuickDateFilterOption: boolean;
  @Input() showResetFilterOption: boolean;
  @Input() isLoading: boolean;
  @Input() filterVm$: Observable<FilterBaseVM>;
  @Input() platformType: PlatformEnum;
  @Input() title: string;
  @Input() isWindows: boolean;
  @Input() quickFilters: QuickFilter[] = [];
  @Input() showFilters = true;
  @Input() defaultFilterId: AllFilterIds;
  @Input() defaultDateFilterId: AllFilterIds;
  @Input() modalType: ModalType = ModalType.invoice;
  @Input() showShare = false;
  @Input() showISRFilter = false;
  @Input() showOrderNumberFilter = false;

  @Output() resetFiltersEmitter = new EventEmitter<void>();
  @Output() downloadAllEmitter = new EventEmitter<void>();
  @Output() printAllEmitter = new EventEmitter<void>();
  @Output() toggleStatusEmitter = new EventEmitter<Filter[]>();
  @Output() shareEmitter = new EventEmitter<Event>();
  @Output() shareDownloadEmitter = new EventEmitter<Event>();
  @Output() filtersWillChangeEmitter = new EventEmitter<void>();

  readonly PlatformEnum = PlatformEnum;
  readonly HeaderFilterButtonStatus = HeaderFilterButtonStatus;

  filterVmSub$: Subscription;

  constructor(private paymentsModalService: PaymentsModalService) {}

  ngOnInit(): void {
    this.setQuickFilters();
  }

  ngOnDestroy() {
    this.filterVmSub$?.unsubscribe();
  }

  async openModal(filterSelected?: AllFilterIds) {
    this.paymentsModalService.openModal(
      this.service,
      filterSelected,
      this.showISRFilter,
      this.modalType,
      this.showOrderNumberFilter,
      false,
      [],
      false,
      () => this.filtersWillChangeEmitter.emit(),
    );
  }

  setQuickFilters() {
    const statusConverter = {
      [Status.OVERDUE]: 'totalOverDue',
      [Status.PARTIALLY_PAID]: 'totalPartialPaid',
      [Status.UNPAID]: 'totalUnpaid',
      [Status.PENDING]: 'totalPending',
      [Status.PROCESSING]: 'totalProcessing',
      [Status.PAID]: 'totalPaid',
      [Status.APPLIED]: 'totalApplied',
      [Status.CANCELLING]: 'totalCancelling',
      [Status.UNUSED]: 'totalUnused',
      [Status.AUTOPAY]: 'totalAutopay',
    };

    this.filterVmSub$ = this.filterVm$.subscribe(viewModel => {
      if (viewModel.statusSummary?.loading) {
        return;
      }

      const statusFilter =
        (viewModel.filters.find(filter => {
          return filter.filterName === 'status';
        })?.value as string[]) || [];

      this.quickFilters.forEach(quickFilter => {
        quickFilter.selected = quickFilter.id.every(filterName =>
          statusFilter.includes(filterName),
        );
        quickFilter.count = quickFilter.id.reduce(
          (acc, status: Status) =>
            acc + (viewModel.statusSummary[statusConverter[status]] || 0),
          0,
        );
      });
      this.quickFilters = this.quickFilters.slice(0);
    });
  }

  toggleStatusFilter(quickFilter: QuickFilter) {
    const multipleStatusSelected =
      this.quickFilters.filter(filter => filter.selected).length > 1;

    const selectedUnselectedStatus = !this.quickFilters.filter(
      filter => filter.id === quickFilter.id,
    )[0].selected;

    let newFilters = this.filters.filter(
      filter => filter.filterName !== FiltersIds.Status,
    );

    if (multipleStatusSelected || selectedUnselectedStatus) {
      newFilters.push({
        filterName: FiltersIds.Status,
        value: [].concat(quickFilter.id),
      } as Filter);
    }

    this.toggleStatusEmitter.emit(newFilters);
  }

  downloadAll() {
    this.downloadAllEmitter.emit();
  }

  printAll() {
    this.printAllEmitter.emit();
  }

  resetFilters() {
    this.service.clearFiltersAndResetSort();
    this.resetFiltersEmitter.emit();
  }

  share() {
    this.shareEmitter.emit();
  }

  shareDownload() {
    this.shareDownloadEmitter.emit();
  }
}
